/**
 * Feature Discovery Context
 *
 * Provides feature discovery state across the application
 * for contextual micro-onboarding.
 *
 * Key features:
 * - Only ONE tooltip visible at a time (singleton pattern)
 * - Can be globally disabled (e.g., when loading demo)
 * - Persists to localStorage
 * - Auto-completes after user has seen a few features
 */

import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { useFeatureDiscovery, type UseFeatureDiscoveryReturn, type FeatureId, FEATURE_INFO } from '../hooks/useFeatureDiscovery';
import { StorageService } from '../services/storage';

interface FeatureDiscoveryContextValue extends UseFeatureDiscoveryReturn {
  /** Currently active tooltip (only one at a time) */
  activeTooltip: FeatureId | null;
  /** Request to show a tooltip (will be ignored if another is active or feature already seen) */
  requestTooltip: (featureId: FeatureId) => void;
  /** Dismiss the active tooltip */
  dismissActiveTooltip: () => void;
  /** Globally disable all tooltips */
  disableTooltips: () => void;
  /** Re-enable tooltips */
  enableTooltips: () => void;
  /** Check if tooltips are globally disabled */
  areTooltipsDisabled: boolean;
  /** Mark all features as discovered (for demo mode) */
  markAllAsDiscovered: () => void;
  /** Check if user has completed initial onboarding */
  hasCompletedOnboarding: boolean;
  /** Mark onboarding as complete */
  completeOnboarding: () => void;
}

const FeatureDiscoveryContext = createContext<FeatureDiscoveryContextValue | null>(null);

const TOOLTIPS_DISABLED_KEY = 'feature_tooltips_disabled';
const ONBOARDING_COMPLETE_KEY = 'feature_onboarding_complete';

interface FeatureDiscoveryProviderProps {
  children: ReactNode;
}

/**
 * Provider component for feature discovery state
 */
export function FeatureDiscoveryProvider({ children }: FeatureDiscoveryProviderProps) {
  const featureDiscovery = useFeatureDiscovery();

  // Active tooltip - only ONE at a time (singleton)
  const [activeTooltip, setActiveTooltip] = useState<FeatureId | null>(null);

  // Global disable flag
  const [areTooltipsDisabled, setAreTooltipsDisabled] = useState(() => {
    return StorageService.get<boolean>(TOOLTIPS_DISABLED_KEY, false);
  });

  // Onboarding complete flag
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(() => {
    return StorageService.get<boolean>(ONBOARDING_COMPLETE_KEY, false);
  });

  // Request to show a tooltip - only if no other is active and feature not already seen
  const requestTooltip = useCallback((featureId: FeatureId) => {
    // Don't show if:
    // - Tooltips are globally disabled
    // - Onboarding is complete
    // - Another tooltip is already active
    // - This feature was already discovered
    if (areTooltipsDisabled) return;
    if (hasCompletedOnboarding) return;
    if (activeTooltip !== null) return; // Already showing a tooltip
    if (!featureDiscovery.isFirstTime(featureId)) return;

    setActiveTooltip(featureId);
  }, [activeTooltip, areTooltipsDisabled, hasCompletedOnboarding, featureDiscovery]);

  // Dismiss active tooltip and mark as discovered
  const dismissActiveTooltip = useCallback(() => {
    if (activeTooltip) {
      featureDiscovery.markAsDiscovered(activeTooltip);
      setActiveTooltip(null);
    }
  }, [activeTooltip, featureDiscovery]);

  // Globally disable tooltips
  const disableTooltips = useCallback(() => {
    setAreTooltipsDisabled(true);
    setActiveTooltip(null);
    StorageService.set(TOOLTIPS_DISABLED_KEY, true);
  }, []);

  // Re-enable tooltips
  const enableTooltips = useCallback(() => {
    setAreTooltipsDisabled(false);
    StorageService.set(TOOLTIPS_DISABLED_KEY, false);
  }, []);

  // Mark all features as discovered (useful for demo mode)
  const markAllAsDiscovered = useCallback(() => {
    (Object.keys(FEATURE_INFO) as FeatureId[]).forEach(featureId => {
      featureDiscovery.markAsDiscovered(featureId);
    });
    setActiveTooltip(null);
  }, [featureDiscovery]);

  // Complete onboarding (no more tooltips will show)
  const completeOnboarding = useCallback(() => {
    setHasCompletedOnboarding(true);
    setActiveTooltip(null);
    StorageService.set(ONBOARDING_COMPLETE_KEY, true);
  }, []);

  // Auto-complete onboarding after user has seen 3 features
  useEffect(() => {
    if (!hasCompletedOnboarding && featureDiscovery.discoveredCount >= 3) {
      completeOnboarding();
    }
  }, [featureDiscovery.discoveredCount, hasCompletedOnboarding, completeOnboarding]);

  const value: FeatureDiscoveryContextValue = {
    ...featureDiscovery,
    activeTooltip,
    requestTooltip,
    dismissActiveTooltip,
    disableTooltips,
    enableTooltips,
    areTooltipsDisabled,
    markAllAsDiscovered,
    hasCompletedOnboarding,
    completeOnboarding,
  };

  return (
    <FeatureDiscoveryContext.Provider value={value}>
      {children}
    </FeatureDiscoveryContext.Provider>
  );
}

/**
 * Hook to access feature discovery context
 */
export function useFeatureDiscoveryContext(): FeatureDiscoveryContextValue {
  const context = useContext(FeatureDiscoveryContext);

  if (!context) {
    throw new Error(
      'useFeatureDiscoveryContext must be used within a FeatureDiscoveryProvider'
    );
  }

  return context;
}

/**
 * Optional hook that returns null if not in context (for gradual adoption)
 */
export function useOptionalFeatureDiscovery(): FeatureDiscoveryContextValue | null {
  return useContext(FeatureDiscoveryContext);
}
